import { useEffect, useRef } from 'react';
import {
  AreaSeries, ColorType, CrosshairMode, LineSeries, LineStyle, createChart,
  type IChartApi, type ISeriesApi, type Time, type UTCTimestamp,
} from 'lightweight-charts';
import { getLang, localeOf } from '../lib/i18n';
import { haptic } from '../lib/telegram';

/**
 * The Money screen's line: money kept so far in the range, as it built up.
 *
 * A running total reads like a balance — where you are by today, and
 * where you were by the same day last time (the dotted line). Days come
 * from /api/coach/stats; «День» uses its hourly figures for today and
 * yesterday. Everything else is arithmetic here, as the bars were.
 */

export type Range = 'day' | 'week' | 'month' | 'year' | 'all';
export type Span = 'hour' | 'day' | 'week' | 'month';

/** «Как получено» (server: routes/stats.ts): `collected` split by how it was paid. */
type Methods = Record<string, { amount: number; n: number }>;
interface DayIn { date: string; earned: number; collected: number; court?: number; sessions: number; methods?: Methods }
interface HourIn { date: string; hour: number; earned: number; collected: number; court: number; sessions: number; methods?: Methods }

/** One point on the line: the slot's first day, and its time on the chart. */
export interface Slot { date: string; hour?: number; time: Time; span: Span }
/** Running totals up to and including a slot. */
export interface Cum { kept: number; earned: number; court: number; collected: number; sessions: number; methods: Methods }

/** a + b, method by method; neither is changed. */
const addMethods = (a: Methods, b?: Methods): Methods => {
  if (!b) return a;
  const out: Methods = { ...a };
  for (const [k, v] of Object.entries(b)) {
    const was = out[k] ?? { amount: 0, n: 0 };
    out[k] = { amount: Math.round((was.amount + Number(v.amount)) * 100) / 100, n: was.n + Number(v.n) };
  }
  return out;
};

export interface Built {
  slots: Slot[];
  /** Index of the slot that holds now; later slots are still to come. */
  upto: number;
  cur: Cum[];
  /** The previous period by the same index, or null for «Всё». */
  prev: Cum[] | null;
  prevSlots: Slot[];
}

// Coaching hours (as on «Записать»): earlier and later hours fold into the ends.
const FIRST_HOUR = 7;
const LAST_HOUR = 22;

const pad = (n: number) => String(n).padStart(2, '0');
const keyOf = (d: Date) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
const parse = (k: string) => { const [y, m, d] = k.split('-').map(Number); return new Date(Date.UTC(y, m - 1, d)); };
const addDays = (k: string, n: number) => { const d = parse(k); d.setUTCDate(d.getUTCDate() + n); return keyOf(d); };
export const mondayOf = (k: string) => { const d = parse(k); return addDays(k, -((d.getUTCDay() + 6) % 7)); };
const monthOf = (k: string) => `${k.slice(0, 7)}-01`;
const addMonths = (k: string, n: number) => {
  const d = parse(monthOf(k)); d.setUTCMonth(d.getUTCMonth() + n); return keyOf(d);
};
const daysIn = (k: string) => { const d = parse(monthOf(k)); return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate(); };

/** Today on this device, as the calendar below counts it. */
export const todayKey = () => {
  const n = new Date();
  return `${n.getFullYear()}-${pad(n.getMonth() + 1)}-${pad(n.getDate())}`;
};

function daySlots(from: string, to: string, span: Span): Slot[] {
  const out: Slot[] = [];
  const step = (k: string) => span === 'day' ? addDays(k, 1) : span === 'week' ? addDays(k, 7) : addMonths(k, 1);
  for (let k = from; k <= to; k = step(k)) out.push({ date: k, time: k as Time, span });
  return out;
}

/** The slots of the current period (back = 0) or the one before (back = 1). */
function slotsFor(range: Exclude<Range, 'all'>, today: string, back: number): Slot[] {
  if (range === 'day') {
    const date = addDays(today, -back);
    const d = parse(today); // chart times always on today's axis
    return Array.from({ length: LAST_HOUR - FIRST_HOUR + 1 }, (_, i) => ({
      date, hour: FIRST_HOUR + i, span: 'hour' as Span,
      time: (Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), FIRST_HOUR + i) / 1000) as UTCTimestamp,
    }));
  }
  if (range === 'week') {
    const mon = addDays(mondayOf(today), -7 * back);
    return daySlots(mon, addDays(mon, 6), 'day');
  }
  if (range === 'month') {
    const first = addMonths(today, -back);
    return daySlots(first, addDays(first, daysIn(first) - 1), 'day');
  }
  const year = Number(today.slice(0, 4)) - back;
  return daySlots(mondayOf(`${year}-01-01`), `${year}-12-31`, 'week');
}

function cumulate(
  slots: Slot[], days: DayIn[], hours: HourIn[], useCourt: boolean, bounds?: [string, string],
): Cum[] {
  const index = new Map(slots.map((s, i) => [s.hour === undefined ? s.date : `${s.date} ${s.hour}`, i]));
  const per: Cum[] = slots.map(() => ({ kept: 0, earned: 0, court: 0, collected: 0, sessions: 0, methods: {} }));
  const add = (i: number | undefined, v: DayIn) => {
    if (i === undefined) return;
    const court = Number(v.court ?? 0);
    per[i].earned += v.earned; per[i].court += court;
    per[i].kept += useCourt ? v.earned - court : v.earned;
    per[i].collected += v.collected; per[i].sessions += v.sessions;
    // The same rows as `collected`, so «Как получено» always adds up to «Получено».
    per[i].methods = addMethods(per[i].methods, v.methods);
  };
  const span = slots[0]?.span;
  if (span === 'hour') {
    for (const h of hours) {
      const hour = Math.min(LAST_HOUR, Math.max(FIRST_HOUR, h.hour));
      add(index.get(`${h.date} ${hour}`), h);
    }
  } else {
    for (const d of days) {
      if (bounds && (d.date < bounds[0] || d.date > bounds[1])) continue;
      const k = span === 'week' ? mondayOf(d.date) : span === 'month' ? monthOf(d.date) : d.date;
      add(index.get(k), d);
    }
  }
  let run: Cum = { kept: 0, earned: 0, court: 0, collected: 0, sessions: 0, methods: {} };
  return per.map((p) => (run = {
    kept: run.kept + p.kept, earned: run.earned + p.earned, court: run.court + p.court,
    collected: run.collected + p.collected, sessions: run.sessions + p.sessions,
    methods: addMethods(run.methods, p.methods),
  }));
}

export function buildSeries(
  days: DayIn[], hours: HourIn[], range: Range, useCourt: boolean,
): Built {
  const today = todayKey();
  const nowHour = Math.min(LAST_HOUR, Math.max(FIRST_HOUR, new Date().getHours()));

  if (range === 'all') {
    // From the first day anything was taught; daily for a short history,
    // then weekly, then monthly so the line stays readable.
    const first = days.find((d) => d.sessions > 0)?.date ?? today;
    const length = (parse(today).getTime() - parse(first).getTime()) / 86400000;
    const span: Span = length <= 120 ? 'day' : length <= 3 * 365 ? 'week' : 'month';
    const from = span === 'week' ? mondayOf(first) : span === 'month' ? monthOf(first) : first;
    const slots = daySlots(from, today, span);
    const cur = cumulate(slots, days, [], useCourt, [first, today]);
    return { slots, upto: slots.length - 1, cur, prev: null, prevSlots: [] };
  }

  const slots = slotsFor(range, today, 0);
  const prevSlots = slotsFor(range, today, 1);
  const yearOf = (s: Slot[]) => [`${s[s.length - 1].date.slice(0, 4)}-01-01`, `${s[s.length - 1].date.slice(0, 4)}-12-31`] as [string, string];
  const bounds = range === 'year' ? yearOf(slots) : undefined;
  const prevBounds = range === 'year' ? yearOf(prevSlots) : undefined;

  let upto = 0;
  slots.forEach((s, i) => {
    if (s.hour !== undefined ? s.hour <= nowHour : s.date <= today) upto = i;
  });

  return {
    slots, upto, prevSlots,
    cur: cumulate(slots, days, hours, useCourt, bounds).slice(0, upto + 1),
    prev: cumulate(prevSlots, days, hours, useCourt, prevBounds),
  };
}

/** The previous period's point to compare index i with (its last, if shorter). */
export const prevAt = (b: Built, i: number) =>
  b.prev && b.prev.length ? Math.min(i, b.prev.length - 1) : -1;

const css = (name: string) =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim();

/** Any CSS colour → rgba with alpha, via the canvas (the theme hands us hex or Telegram vars). */
function withAlpha(colour: string, a: number): string {
  const c = document.createElement('canvas').getContext('2d');
  if (!c) return colour;
  c.fillStyle = colour;
  const hex = c.fillStyle as string;
  if (!hex.startsWith('#')) return colour;
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}

function tickFormatter(range: Range, span: Span) {
  const loc = localeOf(getLang());
  const fmt = (o: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat(loc, { ...o, timeZone: 'UTC' });
  const hour = fmt({ hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  const weekday = fmt({ weekday: 'short' });
  const dayMonth = fmt({ day: 'numeric', month: 'short' });
  const month = fmt({ month: 'short' });
  const monthYear = fmt({ month: 'short', year: '2-digit' });
  return (time: Time) => {
    const d = typeof time === 'number' ? new Date(time * 1000) : parse(String(time));
    if (range === 'day') return hour.format(d);
    if (range === 'week') return weekday.format(d);
    if (range === 'month') return dayMonth.format(d);
    if (range === 'year') return month.format(d);
    return span === 'day' ? dayMonth.format(d) : monthYear.format(d);
  };
}

export function MoneyChart({
  built, range, ahead, showKept, showIn, onScrub, onTap,
}: {
  built: Built;
  range: Range;
  /** Ahead of the dotted line by now: green line, otherwise grey. */
  ahead: boolean;
  showKept: boolean;
  showIn: boolean;
  onScrub: (i: number | null) => void;
  onTap: (i: number) => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const keptRef = useRef<ISeriesApi<'Area'> | null>(null);
  const inRef = useRef<ISeriesApi<'Line'> | null>(null);
  const builtRef = useRef(built);
  builtRef.current = built;
  const cb = useRef({ onScrub, onTap });
  cb.current = { onScrub, onTap };

  useEffect(() => {
    const el = box.current!;
    const hint = css('--hint') || '#79838f';
    const green = css('--court') || '#0e9b8c';
    const line = ahead ? green : hint;

    const chart = createChart(el, {
      autoSize: true,
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: withAlpha(hint, 0.75), fontSize: 10,
        fontFamily: getComputedStyle(document.body).fontFamily,
        // The licence's credit is the link under the legend instead (MoneyTab),
        // so the logo doesn't sit on the line.
        attributionLogo: false,
      },
      grid: { vertLines: { visible: false }, horzLines: { visible: false } },
      rightPriceScale: { visible: false, scaleMargins: { top: 0.12, bottom: 0.04 } },
      leftPriceScale: { visible: false },
      timeScale: {
        borderVisible: false, fixLeftEdge: true, fixRightEdge: true,
        lockVisibleTimeRangeOnResize: true, timeVisible: range === 'day',
        tickMarkFormatter: tickFormatter(range, built.slots[0]?.span ?? 'day'),
      },
      crosshair: {
        mode: CrosshairMode.Magnet,
        vertLine: { color: withAlpha(hint, 0.45), width: 1, style: LineStyle.Solid, labelVisible: false },
        horzLine: { visible: false, labelVisible: false },
      },
      handleScroll: false,
      handleScale: false,
    });
    chartRef.current = chart;

    const blank = { lastValueVisible: false, priceLineVisible: false };
    const { slots, upto, cur, prev } = built;
    const real = (i: number) => i <= upto;

    if (prev) {
      const compare = chart.addSeries(LineSeries, {
        ...blank, color: withAlpha(hint, 0.55), lineWidth: 2, lineStyle: LineStyle.Dotted,
        crosshairMarkerVisible: false,
      });
      compare.setData(slots.map((s, i) =>
        i < prev.length ? { time: s.time, value: prev[i].kept } : { time: s.time }));
    }

    const received = chart.addSeries(LineSeries, {
      ...blank, color: css('--held') || '#b3801d', lineWidth: 1,
      crosshairMarkerRadius: 3, visible: showIn,
    });
    received.setData(slots.map((s, i) => real(i) ? { time: s.time, value: cur[i].collected } : { time: s.time }));
    inRef.current = received;

    const kept = chart.addSeries(AreaSeries, {
      ...blank, lineColor: line, lineWidth: 2,
      topColor: withAlpha(line, ahead ? 0.32 : 0.14), bottomColor: withAlpha(line, 0),
      crosshairMarkerRadius: 5, crosshairMarkerBorderColor: css('--surface') || '#fff',
      crosshairMarkerBorderWidth: 2, crosshairMarkerBackgroundColor: line,
      visible: showKept,
    });
    kept.setData(slots.map((s, i) => real(i) ? { time: s.time, value: cur[i].kept } : { time: s.time }));
    keptRef.current = kept;

    chart.timeScale().fitContent();
    return () => { chart.remove(); chartRef.current = null; };
    // The chart is rebuilt per range and per data; visibility is applied below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [built, range, ahead]);

  useEffect(() => { keptRef.current?.applyOptions({ visible: showKept }); }, [showKept]);
  useEffect(() => { inRef.current?.applyOptions({ visible: showIn }); }, [showIn]);

  // Scrubbing is handled here rather than by the chart, so a drag on a
  // phone moves the dot at once (the chart's own touch needs a long press).
  const drag = useRef<{ x: number; at: number; moved: boolean; i: number } | null>(null);

  const indexAt = (clientX: number): number | null => {
    const chart = chartRef.current;
    if (!chart || !box.current) return null;
    const x = clientX - box.current.getBoundingClientRect().left;
    const logical = chart.timeScale().coordinateToLogical(x);
    if (logical === null) return null;
    return Math.max(0, Math.min(builtRef.current.upto, Math.round(logical)));
  };

  const show = (i: number) => {
    const b = builtRef.current;
    const series = keptRef.current?.options().visible ? keptRef.current : inRef.current;
    if (!series || !chartRef.current) return;
    const value = series === keptRef.current ? b.cur[i].kept : b.cur[i].collected;
    chartRef.current.setCrosshairPosition(value, b.slots[i].time, series);
    if (drag.current && drag.current.i !== i) haptic.tap();
    if (drag.current) drag.current.i = i;
    cb.current.onScrub(i);
  };

  const end = (tap: boolean) => {
    const d = drag.current;
    drag.current = null;
    chartRef.current?.clearCrosshairPosition();
    cb.current.onScrub(null);
    if (tap && d) cb.current.onTap(d.i);
  };

  return (
    <div className="mchart">
      <div className="mchart__plot" ref={box} />
      <div className="mchart__touch"
        onPointerDown={(e) => {
          const i = indexAt(e.clientX);
          if (i === null) return;
          e.currentTarget.setPointerCapture(e.pointerId);
          drag.current = { x: e.clientX, at: Date.now(), moved: false, i: -1 };
          show(i);
        }}
        onPointerMove={(e) => {
          if (!drag.current) return;
          if (Math.abs(e.clientX - drag.current.x) > 6) drag.current.moved = true;
          const i = indexAt(e.clientX);
          if (i !== null) show(i);
        }}
        onPointerUp={() => {
          const d = drag.current;
          end(!!d && !d.moved && Date.now() - d.at < 500);
        }}
        onPointerCancel={() => end(false)}
      />
    </div>
  );
}
